import moment from 'moment';

const PICKUP_HOUR_TYPES = ['Retail Hours', 'Pickup Hours'];
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const parseTime = (timeStr) => {
  if (!timeStr) {
    return null;
  }

  const normalized = timeStr.trim().replace(/\s+/g, ' ');
  const formats = ['h:mm A', 'h:mmA', 'h A', 'hA', 'hh:mm A', 'hh:mmA'];

  for (let i = 0; i < formats.length; i += 1) {
    const parsed = moment(normalized, formats[i], true);
    if (parsed.isValid()) {
      return parsed;
    }
  }

  return null;
};

const parseHourRange = (hourStr) => {
  if (!hourStr || hourStr.toUpperCase().includes('CLOSED')) {
    return null;
  }

  const parts = hourStr.split('-').map(part => part.trim());
  if (parts.length !== 2) {
    return null;
  }

  const open = parseTime(parts[0]);
  const close = parseTime(parts[1]);

  if (!open || !close) {
    return null;
  }

  return { open, close };
};

const getPickupHourDays = (hours) => {
  if (!hours?.length) {
    return [];
  }

  const pickupHours = hours.find(entry => PICKUP_HOUR_TYPES.includes(entry.type));
  return pickupHours?.days || [];
};

const toMinutes = (timeMoment) => timeMoment.hours() * 60 + timeMoment.minutes();

const formatTimeLabel = (timeMoment) =>
  timeMoment.format('h:mm A').replace(':00', '');

const formatDayAbbrev = (dayName) => dayName.slice(0, 3);

const isOpenForRange = (range, nowMinutes) => {
  const openMinutes = toMinutes(range.open);
  const closeMinutes = toMinutes(range.close);

  if (closeMinutes <= openMinutes) {
    // Overnight range evaluated for *today*: only the pre-midnight portion counts
    // as open now. The early-morning tail belongs to the previous day's entry,
    // which is handled separately before this check.
    return nowMinutes >= openMinutes;
  }

  return nowMinutes >= openMinutes && nowMinutes < closeMinutes;
};

const getDayEntry = (days, dayName) => days.find(entry => entry.day === dayName);

const getDayIndex = (dayName) => DAY_NAMES.indexOf(dayName);

const getNextOpenInfo = (days, startDayIndex, nowMinutes) => {
  for (let offset = 0; offset < DAY_NAMES.length; offset += 1) {
    const dayIndex = (startDayIndex + offset) % DAY_NAMES.length;
    const dayName = DAY_NAMES[dayIndex];
    const entry = getDayEntry(days, dayName);
    const range = entry ? parseHourRange(entry.hour) : null;

    if (range) {
      const openMinutes = toMinutes(range.open);

      if (offset === 0 && nowMinutes < openMinutes) {
        return {
          openTime: range.open,
          dayName,
          isToday: true,
        };
      }

      if (offset > 0) {
        return {
          openTime: range.open,
          dayName,
          isToday: false,
        };
      }
    }
  }

  return null;
};

/**
 * Derives pickup open/closed state and display labels from store hours.
 * Falls back to closed when hours are missing or unparseable.
 */
export const getStorePickupHoursStatus = (hours, referenceDate = moment()) => {
  const days = getPickupHourDays(hours);
  const now = moment(referenceDate);
  const nowMinutes = now.hours() * 60 + now.minutes();
  const todayName = now.format('dddd');
  const todayIndex = getDayIndex(todayName);

  if (!days.length) {
    return { isOpen: false, statusText: 'Closed', hoursText: '' };
  }

  const previousDayName = DAY_NAMES[(todayIndex + DAY_NAMES.length - 1) % DAY_NAMES.length];
  const previousEntry = getDayEntry(days, previousDayName);
  const previousRange = previousEntry ? parseHourRange(previousEntry.hour) : null;

  if (previousRange && toMinutes(previousRange.close) <= toMinutes(previousRange.open)) {
    if (nowMinutes < toMinutes(previousRange.close)) {
      return {
        isOpen: true,
        statusText: 'Open',
        hoursText: `Closes ${formatTimeLabel(previousRange.close)}`,
      };
    }
  }

  const todayEntry = getDayEntry(days, todayName);
  const todayRange = todayEntry ? parseHourRange(todayEntry.hour) : null;

  if (todayRange && isOpenForRange(todayRange, nowMinutes)) {
    return {
      isOpen: true,
      statusText: 'Open',
      hoursText: `Closes ${formatTimeLabel(todayRange.close)}`,
    };
  }

  const nextOpen = getNextOpenInfo(days, todayIndex, nowMinutes);

  if (nextOpen) {
    const daySuffix = nextOpen.isToday ? '' : ` ${formatDayAbbrev(nextOpen.dayName)}`;
    return {
      isOpen: false,
      statusText: 'Closed',
      hoursText: `Opens ${formatTimeLabel(nextOpen.openTime)}${daySuffix}`,
    };
  }

  return { isOpen: false, statusText: 'Closed', hoursText: '' };
};
